import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Tournament {
    CLAVE: number;
    NOMBRE: string;
    FECHAINICIO: string;
    FECHATERMINO: string;
    BOWLER_COUNT: number;
    CVEBOLICHE: number;
}

interface EntryBowler {
    CLAVE: number;
    FICHANAL: number;
    NOMBRECOMPLETO: string;
    NOMBRE: string;
    APELLIDOP: string;
    EMAIL: string;
    USBCNUM?: string | null;
    PACKAGE_COUNT: number;
    CVERECRUITER: number | null;
    RECRUITER_NAME: string | null;
}

interface EntryPackage {
    CVEJUGADOR: number;
    CVEPAQUETE: number;
    DESCRIPCION: string;
    COSTO: number;
}

interface TournamentStats {
    TOTAL_BOWLERS: number;
    TOTAL_PACKAGES: number;
    TOTAL_AMOUNT: number;
    TOTAL_TAX: number;
    TOTAL_PRIZE: number;
    TOTAL_LANES: number;
}

interface BowlerBalance {
    COMPRADO: number;
    PAGADO: number;
    BALANCE: number;
    FOURCC?: string | null;
    loading: boolean;
}

const PAGE_SIZE = 25;

const AdminTournamentEntries: React.FC = () => {
    const navigate = useNavigate();

    // Auth guard
    useEffect(() => {
        if (!localStorage.getItem('loggedInAdmin')) navigate('/admin');
    }, [navigate]);

    // ── Tournament list ────────────────────────────────────────────────
    const [tournaments, setTournaments] = useState<Tournament[]>([]);
    const [tournamentsLoading, setTournamentsLoading] = useState(true);
    const [tournamentError, setTournamentError] = useState('');
    const [tournamentFilter, setTournamentFilter] = useState<'all' | 'summer' | 'nationals' | 'regionals'>(
        () => (sessionStorage.getItem('adminTournamentFilter') as 'all' | 'summer' | 'nationals' | 'regionals') || 'all'
    );

    const handleSetFilter = (f: 'all' | 'summer' | 'nationals' | 'regionals') => {
        setTournamentFilter(f);
        sessionStorage.setItem('adminTournamentFilter', f);
    };

    const filteredTournaments = tournaments.filter(t => {
        const name = t.NOMBRE.toLowerCase();
        if (tournamentFilter === 'summer') return name.includes('summer');
        if (tournamentFilter === 'nationals') return name.includes('national');
        if (tournamentFilter === 'regionals') return !name.includes('summer') && !name.includes('national');
        return true;
    });

    // ── Bowler list ────────────────────────────────────────────────────
    const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
    const [bowlers, setBowlers] = useState<EntryBowler[]>([]);
    const [packages, setPackages] = useState<EntryPackage[]>([]);
    const [stats, setStats] = useState<TournamentStats>({ TOTAL_BOWLERS: 0, TOTAL_PACKAGES: 0, TOTAL_AMOUNT: 0, TOTAL_TAX: 0, TOTAL_PRIZE: 0, TOTAL_LANES: 0 });
    const [taxRate, setTaxRate] = useState(19);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [bowlersLoading, setBowlersLoading] = useState(false);
    const [bowlerError, setBowlerError] = useState('');
    const [balances, setBalances] = useState<Record<number, BowlerBalance>>({});
    const [bowlerTaxes, setBowlerTaxes] = useState<Record<number, number>>({});
    const [removingId, setRemovingId] = useState<number | null>(null);

    // ── Recruiter assignment ───────────────────────────────────────────
    const [discountCodeBowlers, setDiscountCodeBowlers] = useState<Set<number>>(new Set());
    const [tournamentRecruiters, setTournamentRecruiters] = useState<any[]>([]);
    const [recruiterPopover, setRecruiterPopover] = useState<number | null>(null);
    const [settingRecruiter, setSettingRecruiter] = useState<number | null>(null);
    const [bowlerRecruiterMap, setBowlerRecruiterMap] = useState<Record<number, { clave: number | null; name: string | null }>>({});

    // ── Add Recruiter to Tournament modal ─────────────────────────────
    const [showAddRecruiterModal, setShowAddRecruiterModal] = useState(false);
    const [recruiterCandidates, setRecruiterCandidates] = useState<any[]>([]);
    const [recruiterCandidatesTotal, setRecruiterCandidatesTotal] = useState(0);
    const [recruiterCandidatesPage, setRecruiterCandidatesPage] = useState(1);
    const [recruiterCandidatesSearch, setRecruiterCandidatesSearch] = useState('');
    const [recruiterCandidatesLoading, setRecruiterCandidatesLoading] = useState(false);
    const [addingRecruiter, setAddingRecruiter] = useState<number | null>(null);
    const recruiterSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ── Add Bowler to Tournament modal ────────────────────────────────
    const [showAddBowlerModal, setShowAddBowlerModal] = useState(false);
    const [addBowlerStep, setAddBowlerStep] = useState<'search' | 'division' | 'success'>('search');
    const [addBowlerQuery, setAddBowlerQuery] = useState('');
    const [addBowlerCandidates, setAddBowlerCandidates] = useState<any[]>([]);
    const [addBowlerCandidatesTotal, setAddBowlerCandidatesTotal] = useState(0);
    const [addBowlerCandidatesPage, setAddBowlerCandidatesPage] = useState(1);
    const [addBowlerCandidatesLoading, setAddBowlerCandidatesLoading] = useState(false);
    const [selectedBowlerToAdd, setSelectedBowlerToAdd] = useState<any>(null);
    const [tournamentDivisions, setTournamentDivisions] = useState<any[]>([]);
    const [divisionsLoading, setDivisionsLoading] = useState(false);
    const [selectedDivisionToAdd, setSelectedDivisionToAdd] = useState<number | null>(null);
    const [addingBowler, setAddingBowler] = useState(false);
    const [addBowlerError, setAddBowlerError] = useState('');
    const addBowlerSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ── Load tournament list ──────────────────────────────────────────
    useEffect(() => {
        const load = async () => {
            setTournamentsLoading(true);
            setTournamentError('');
            try {
                const res = await fetch(`${BACKEND_URL}/api/admin/tournaments-with-counts`);
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || 'Failed to load');
                setTournaments(data || []);
            } catch (e: any) {
                setTournamentError(e.message || 'Failed to load tournaments');
            } finally {
                setTournamentsLoading(false);
            }
        };
        load();
    }, []);

    // ── Load bowlers for selected tournament ──────────────────────────
    const loadBowlers = async (torneoId: number, pageNum: number, searchTerm: string) => {
        setBowlersLoading(true);
        setBalances({});
        setBowlerTaxes({});
        setBowlerError('');
        try {
            const params = new URLSearchParams({
                page: String(pageNum),
                pageSize: String(PAGE_SIZE),
            });
            if (searchTerm) params.set('search', searchTerm);

            const res = await fetch(`${BACKEND_URL}/api/admin/tournament-entries/${torneoId}?${params}`);
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to load');

            setBowlers(data.bowlers || []);
            setPackages(data.packages || []);
            setStats(data.stats || { TOTAL_BOWLERS: 0, TOTAL_PACKAGES: 0, TOTAL_AMOUNT: 0 });
            setTotalCount(data.totalCount || 0);
            setDiscountCodeBowlers(new Set(data.discountCodeBowlers || []));
            const recruiterMap: Record<number, { clave: number | null; name: string | null }> = {};
            (data.bowlers || []).forEach((b: EntryBowler) => {
                recruiterMap[b.CLAVE] = { clave: b.CVERECRUITER ?? null, name: b.RECRUITER_NAME ?? null };
            });
            setBowlerRecruiterMap(prev => ({ ...prev, ...recruiterMap }));
            // Build per-bowler tax map
            const taxMap: Record<number, number> = {};
            (data.taxes || []).forEach((t: any) => {
                taxMap[t.CVEJUGADOR] = Number(t.TAX_PAID) || 0;
            });
            setBowlerTaxes(taxMap);
            if (data.bowlers && data.bowlers.length > 0) {
                loadBalances(torneoId, data.bowlers);
            }
        } catch (e: any) {
            setBowlerError(e.message || 'Failed to load entries');
        } finally {
            setBowlersLoading(false);
        }
    };

    const loadBalances = async (torneoId: number, bowlerList: EntryBowler[]) => {
        // Mark all as loading
        const loadingMap: Record<number, BowlerBalance> = {};
        bowlerList.forEach(b => {
            loadingMap[b.CLAVE] = { COMPRADO: 0, PAGADO: 0, BALANCE: 0, loading: true };
        });
        setBalances(loadingMap);

        const results = await Promise.all(
            bowlerList.map(b =>
                fetch(`${BACKEND_URL}/api/finances/balance/${torneoId}/${b.CLAVE}`)
                    .then(r => r.json())
                    .then(d => ({ clave: b.CLAVE, COMPRADO: Number(d.COMPRADO) || 0, PAGADO: Number(d.PAGADO) || 0, BALANCE: Number(d.BALANCE) || 0, FOURCC: d.FOURCC ?? null, loading: false }))
                    .catch(() => ({ clave: b.CLAVE, COMPRADO: 0, PAGADO: 0, BALANCE: 0, FOURCC: null, loading: false }))
            )
        );

        const balanceMap: Record<number, BowlerBalance> = {};
        results.forEach(r => {
            balanceMap[r.clave] = { COMPRADO: r.COMPRADO, PAGADO: r.PAGADO, BALANCE: r.BALANCE, FOURCC: r.FOURCC ?? null, loading: false };
        });
        setBalances(balanceMap);
    };

    // ── Select tournament ─────────────────────────────────────────────
    const handleSelectTournament = (t: Tournament) => {
        setSelectedTournament(t);
        setSearch('');
        setPage(1);
        setTaxRate(19);
        setBowlerError('');
        setBowlerRecruiterMap({});
        setDiscountCodeBowlers(new Set());
        setRecruiterPopover(null);
        loadBowlers(t.CLAVE, 1, '');
        // Load recruiters for this tournament
        fetch(`${BACKEND_URL}/api/admin/recruiters-for-tournament/${t.CLAVE}`)
            .then(r => r.json())
            .then(data => setTournamentRecruiters(data || []))
            .catch(() => setTournamentRecruiters([]));
    };

    // ── Back to tournament list ───────────────────────────────────────
    const handleBackToList = () => {
        setSelectedTournament(null);
        setBowlers([]);
        setPackages([]);
        setBalances({});
        setBowlerTaxes({});
        setSearch('');
        setPage(1);
        setTotalCount(0);
        setBowlerError('');
        setDiscountCodeBowlers(new Set());
        setTournamentRecruiters([]);
        setRecruiterPopover(null);
        setBowlerRecruiterMap({});
    };

    // ── Search with debounce ──────────────────────────────────────────
    const handleSearchChange = (val: string) => {
        setSearch(val);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            if (selectedTournament) {
                setPage(1);
                loadBowlers(selectedTournament.CLAVE, 1, val);
            }
        }, 400);
    };

    // ── Page navigation ───────────────────────────────────────────────
    const handlePageChange = (newPage: number) => {
        if (!selectedTournament) return;
        setPage(newPage);
        loadBowlers(selectedTournament.CLAVE, newPage, search);
    };

    // ── Recruiter popover click-outside close ─────────────────────────
    useEffect(() => {
        if (recruiterPopover === null) return;
        const close = () => setRecruiterPopover(null);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [recruiterPopover]);

    // ── Set recruiter on a bowler's tournament entry ─────────────────
    const handleSetRecruiter = async (jugadorClave: number, torneoClave: number, recruiterClave: number | null, recruiterName: string | null) => {
        setSettingRecruiter(jugadorClave);
        try {
            const resp = await fetch(`${BACKEND_URL}/api/admin/set-recruiter`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ jugadorClave, torneoClave, recruiterClave }),
            });
            if (resp.ok) {
                setBowlerRecruiterMap(prev => ({
                    ...prev,
                    [jugadorClave]: { clave: recruiterClave, name: recruiterName },
                }));
            }
        } finally {
            setSettingRecruiter(null);
            setRecruiterPopover(null);
        }
    };

    // ── Remove bowler ─────────────────────────────────────────────────
    const handleRemoveBowler = async (jugadorClave: number, fichanal: number) => {
        if (!selectedTournament) return;
        if (!window.confirm(`Remove TAT #${fichanal} from "${selectedTournament.NOMBRE}"?\n\nThis cannot be undone.`)) return;
        setRemovingId(jugadorClave);
        try {
            const res = await fetch(
                `${BACKEND_URL}/api/admin/tournament-entry/${selectedTournament.CLAVE}/${jugadorClave}`,
                { method: 'DELETE' }
            );
            const data = await res.json();
            if (res.ok && data.success) {
                loadBowlers(selectedTournament.CLAVE, page, search);
            } else {
                alert(data.error || 'Failed to remove bowler');
            }
        } catch {
            alert('Error removing bowler. Please try again.');
        } finally {
            setRemovingId(null);
        }
    };

    // ── Helpers ───────────────────────────────────────────────────────
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    const isGermanTournament = selectedTournament?.CVEBOLICHE === 17;
    // Simulator: based on total package cost × rate — works for all bowlers past and future
    const displayedTax = isGermanTournament
        ? Number(stats.TOTAL_AMOUNT) * (taxRate / 100)
        : 0;

    const getBowlerName = (b: EntryBowler) =>
        b.NOMBRECOMPLETO || `${b.NOMBRE || ''} ${b.APELLIDOP || ''}`.trim() || 'N/A';

    const getBowlerPackages = (jugadorClave: number) =>
        packages.filter(p => p.CVEJUGADOR === jugadorClave);

    const getBowlerTax = (jugadorClave: number) => bowlerTaxes[jugadorClave] ?? 0;

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch {
            return dateStr;
        }
    };

    const formatCurrency = (amount: number) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

    const loadRecruiterCandidates = async (torneoClave: number, page: number, search: string) => {
        setRecruiterCandidatesLoading(true);
        try {
            const params = new URLSearchParams({ torneoClave: String(torneoClave), page: String(page), search });
            const res = await fetch(`${BACKEND_URL}/api/admin/bowler-recruiter-candidates?${params}`);
            const data = await res.json();
            setRecruiterCandidates(data.rows || []);
            setRecruiterCandidatesTotal(data.total || 0);
        } catch {
            setRecruiterCandidates([]);
        } finally {
            setRecruiterCandidatesLoading(false);
        }
    };

    const handleOpenAddRecruiterModal = () => {
        if (!selectedTournament) return;
        setRecruiterCandidatesPage(1);
        setRecruiterCandidatesSearch('');
        setShowAddRecruiterModal(true);
        loadRecruiterCandidates(selectedTournament.CLAVE, 1, '');
    };

    const handleRecruiterModalSearchChange = (val: string) => {
        setRecruiterCandidatesSearch(val);
        if (recruiterSearchTimer.current) clearTimeout(recruiterSearchTimer.current);
        recruiterSearchTimer.current = setTimeout(() => {
            if (!selectedTournament) return;
            setRecruiterCandidatesPage(1);
            loadRecruiterCandidates(selectedTournament.CLAVE, 1, val);
        }, 350);
    };

    const handleRecruiterCandidatesPageChange = (newPage: number) => {
        if (!selectedTournament) return;
        setRecruiterCandidatesPage(newPage);
        loadRecruiterCandidates(selectedTournament.CLAVE, newPage, recruiterCandidatesSearch);
    };

    const handleAddTournamentRecruiter = async (jugadorClave: number) => {
        if (!selectedTournament) return;
        setAddingRecruiter(jugadorClave);
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/add-tournament-recruiter`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ jugadorClave, torneoClave: selectedTournament.CLAVE }),
            });
            if (res.ok) {
                // Refresh the current page so the badge appears
                loadRecruiterCandidates(selectedTournament.CLAVE, recruiterCandidatesPage, recruiterCandidatesSearch);
                // Also refresh tournamentRecruiters so the "Add to Group" popover stays up to date
                const r = await fetch(`${BACKEND_URL}/api/admin/recruiters-for-tournament/${selectedTournament.CLAVE}`);
                const data = await r.json();
                setTournamentRecruiters(data || []);
            }
        } catch { /* non-critical */ } finally {
            setAddingRecruiter(null);
        }
    };

    // ── Add Bowler modal handlers ─────────────────────────────────────
    const handleOpenAddBowlerModal = () => {
        if (!selectedTournament) return;
        setAddBowlerStep('search');
        setAddBowlerQuery('');
        setAddBowlerCandidates([]);
        setAddBowlerCandidatesTotal(0);
        setAddBowlerCandidatesPage(1);
        setSelectedBowlerToAdd(null);
        setSelectedDivisionToAdd(null);
        setAddBowlerError('');
        setShowAddBowlerModal(true);
    };

    const loadAddBowlerCandidates = async (q: string, page: number) => {
        if (!selectedTournament || q.trim().length < 2) { setAddBowlerCandidates([]); return; }
        setAddBowlerCandidatesLoading(true);
        try {
            const params = new URLSearchParams({ q: q.trim(), torneoClave: String(selectedTournament.CLAVE), page: String(page) });
            const res = await fetch(`${BACKEND_URL}/api/admin/bowler-search?${params}`);
            const data = await res.json();
            setAddBowlerCandidates(data.rows || []);
            setAddBowlerCandidatesTotal(data.total || 0);
        } catch {
            setAddBowlerCandidates([]);
        } finally {
            setAddBowlerCandidatesLoading(false);
        }
    };

    const handleAddBowlerQueryChange = (val: string) => {
        setAddBowlerQuery(val);
        setAddBowlerCandidatesPage(1);
        if (addBowlerSearchTimer.current) clearTimeout(addBowlerSearchTimer.current);
        addBowlerSearchTimer.current = setTimeout(() => loadAddBowlerCandidates(val, 1), 350);
    };

    const handleSelectBowlerToAdd = async (bowler: any) => {
        if (!selectedTournament) return;
        setSelectedBowlerToAdd(bowler);
        setSelectedDivisionToAdd(null);
        setAddBowlerError('');
        setAddBowlerStep('division');
        setDivisionsLoading(true);
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/tournament-divisions/${selectedTournament.CLAVE}`);
            const data = await res.json();
            setTournamentDivisions(Array.isArray(data) ? data : []);
        } catch {
            setTournamentDivisions([]);
        } finally {
            setDivisionsLoading(false);
        }
    };

    const handleAddBowlerSubmit = async () => {
        if (!selectedTournament || !selectedBowlerToAdd || !selectedDivisionToAdd) return;
        setAddingBowler(true);
        setAddBowlerError('');
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/add-bowler-to-tournament`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    jugadorClave: selectedBowlerToAdd.CLAVE,
                    torneoClave: selectedTournament.CLAVE,
                    divisionClave: selectedDivisionToAdd,
                }),
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setAddBowlerStep('success');
                // Refresh the bowler list so the new entry appears
                loadBowlers(selectedTournament.CLAVE, page, search);
            } else {
                setAddBowlerError(data.error || 'Failed to add bowler');
            }
        } catch {
            setAddBowlerError('An error occurred. Please try again.');
        } finally {
            setAddingBowler(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────
    return (
        <>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-100">

            {/* Header */}
            <div className="bg-white shadow-lg">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={selectedTournament ? handleBackToList : () => navigate('/admin/dashboard')}
                            className="text-gray-500 hover:text-gray-800 transition p-2 rounded-lg hover:bg-gray-100"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <img src="/logo.avif" alt="TAT" className="h-10 w-auto" />
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Tournament Entries</h1>
                            {selectedTournament && (
                                <p className="text-sm text-gray-500 leading-tight">{selectedTournament.NOMBRE}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Tournament List View ─────────────────────────────────────── */}
            {!selectedTournament && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {tournamentsLoading ? (
                        <div className="flex justify-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-purple-600" />
                        </div>
                    ) : tournamentError ? (
                        <div className="text-center py-16 text-red-600">{tournamentError}</div>
                    ) : (
                        <>
                        {/* Filter Tabs */}
                        <div className="flex flex-wrap gap-2 mb-6">
                            {(['all', 'summer', 'nationals', 'regionals'] as const).map(f => (
                                <button
                                    key={f}
                                    onClick={() => handleSetFilter(f)}
                                    className={`px-4 py-2 rounded-xl font-semibold text-sm border-2 transition-all ${
                                        tournamentFilter === f
                                            ? 'bg-purple-600 text-white border-purple-600 shadow-md'
                                            : 'bg-white text-gray-600 border-gray-200 hover:border-purple-400 hover:text-purple-600'
                                    }`}
                                >
                                    {f === 'all' ? 'All' : f === 'summer' ? 'Summer Classic' : f === 'nationals' ? 'Nationals' : 'Regionals'}
                                    <span className="ml-2 text-xs opacity-75">
                                        ({f === 'all' ? tournaments.length
                                          : f === 'summer' ? tournaments.filter(t => t.NOMBRE.toLowerCase().includes('summer')).length
                                          : f === 'nationals' ? tournaments.filter(t => t.NOMBRE.toLowerCase().includes('national')).length
                                          : tournaments.filter(t => !t.NOMBRE.toLowerCase().includes('summer') && !t.NOMBRE.toLowerCase().includes('national')).length})
                                    </span>
                                </button>
                            ))}
                        </div>

                        {filteredTournaments.length === 0 ? (
                            <div className="text-center py-16 text-gray-500">No tournaments match this filter.</div>
                        ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {filteredTournaments.map(t => (
                                <div
                                    key={t.CLAVE}
                                    onClick={() => handleSelectTournament(t)}
                                    className="bg-white rounded-2xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition-all transform hover:-translate-y-0.5"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <h2 className="font-bold text-gray-900 text-base leading-snug">{t.NOMBRE}</h2>
                                            <p className="text-sm text-gray-500 mt-1">
                                                {formatDate(t.FECHAINICIO)}
                                                {t.FECHATERMINO && ` — ${formatDate(t.FECHATERMINO)}`}
                                            </p>
                                        </div>
                                        <div className="flex-shrink-0 bg-indigo-100 text-indigo-700 rounded-xl px-3 py-2 text-center min-w-[56px]">
                                            <div className="text-2xl font-bold leading-none">{t.BOWLER_COUNT}</div>
                                            <div className="text-xs font-medium mt-0.5">bowlers</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        )}
                        </>
                    )}
                </div>
            )}

            {/* ── Bowler List View ─────────────────────────────────────────── */}
            {selectedTournament && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

                    {/* Toolbar buttons */}
                    <div className="flex justify-end gap-2 mb-4 flex-wrap">
                        <button
                            onClick={handleOpenAddBowlerModal}
                            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                            </svg>
                            Add Bowler to Tournament
                        </button>
                        <button
                            onClick={handleOpenAddRecruiterModal}
                            className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                            </svg>
                            Add Recruiter to Tournament
                        </button>
                    </div>

                    {/* Stats + Search bar */}
                    <div className="bg-white rounded-2xl shadow-md p-4 mb-5">
                        <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                            {/* Search — full width on mobile, capped on desktop */}
                            <div className="w-full sm:w-64 md:w-72 flex-shrink-0">
                                <input
                                    type="text"
                                    value={search}
                                    onChange={e => handleSearchChange(e.target.value)}
                                    placeholder="Search by name, TAT ID, or email..."
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            {/* Stat chips */}
                            <div className="flex flex-wrap gap-2">
                                <div className="bg-indigo-50 text-indigo-700 rounded-xl px-3 py-2 text-center min-w-[58px]">
                                    <div className="text-lg font-bold leading-none">{stats.TOTAL_BOWLERS}</div>
                                    <div className="text-xs font-medium mt-0.5">Bowlers</div>
                                </div>
                                <div className="bg-purple-50 text-purple-700 rounded-xl px-3 py-2 text-center min-w-[58px]">
                                    <div className="text-lg font-bold leading-none">{stats.TOTAL_PACKAGES}</div>
                                    <div className="text-xs font-medium mt-0.5">Packages</div>
                                </div>
                                <div className="bg-green-50 text-green-700 rounded-xl px-3 py-2 text-center">
                                    <div className="text-sm font-bold leading-none tabular-nums">{formatCurrency(Number(stats.TOTAL_AMOUNT))}</div>
                                    <div className="text-xs font-medium mt-0.5">Sold</div>
                                </div>
                                {Number(stats.TOTAL_PRIZE) > 0 && (
                                    <>
                                        <div className="bg-red-50 text-red-600 rounded-xl px-3 py-2 text-center">
                                            <div className="text-sm font-bold leading-none tabular-nums">{formatCurrency(Number(stats.TOTAL_PRIZE))}</div>
                                            <div className="text-xs font-medium mt-0.5">Prize Out</div>
                                        </div>
                                        <div className="bg-pink-50 text-pink-700 rounded-xl px-3 py-2 text-center">
                                            <div className="text-sm font-bold leading-none tabular-nums">{formatCurrency(Number(stats.TOTAL_AMOUNT) - Number(stats.TOTAL_PRIZE))}</div>
                                            <div className="text-xs font-medium mt-0.5">Net</div>
                                        </div>
                                    </>
                                )}
                                <div className="bg-yellow-50 text-yellow-700 rounded-xl px-3 py-2 text-center min-w-[58px]">
                                    <div className="text-lg font-bold leading-none tabular-nums">{Number(stats.TOTAL_LANES).toLocaleString()}</div>
                                    <div className="text-xs font-medium mt-0.5">Lanes</div>
                                </div>
                                {isGermanTournament && (
                                    <div className="bg-orange-50 text-orange-700 rounded-xl px-3 py-2 text-center">
                                        <div className="text-sm font-bold leading-none tabular-nums">{formatCurrency(displayedTax)}</div>
                                        <div className="text-xs font-medium mt-0.5">Tax ({taxRate}%)</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* German tax rate slider */}
                    {isGermanTournament && (
                        <div className="bg-orange-50 border border-orange-200 rounded-2xl px-5 py-4 mb-5">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-sm font-semibold text-orange-800">
                                            Sales Tax Rate Simulator
                                        </span>
                                        <span className="text-sm font-bold text-orange-700">{taxRate}%</span>
                                    </div>
                                    <input
                                        type="range"
                                        min={1}
                                        max={19}
                                        step={1}
                                        value={taxRate}
                                        onChange={e => setTaxRate(Number(e.target.value))}
                                        className="w-full accent-orange-500 cursor-pointer"
                                    />
                                    <div className="flex justify-between text-xs text-orange-400 mt-0.5">
                                        <span>1%</span>
                                        <span>19% (recorded)</span>
                                    </div>
                                </div>
                                <div className="sm:text-right flex-shrink-0 space-y-0.5">
                                    <div className="text-xs text-orange-500">
                                        Actually collected:&nbsp;
                                        <span className="font-semibold">{formatCurrency(Number(stats.TOTAL_TAX))}</span>
                                    </div>
                                    <div className="text-xs text-orange-700">
                                        At {taxRate}% on total cost:&nbsp;
                                        <span className="font-bold">{formatCurrency(displayedTax)}</span>
                                    </div>
                                    <div className="text-xs text-orange-400 italic mt-1">
                                        No DB changes — display only
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error */}
                    {bowlerError && (
                        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-4">
                            {bowlerError}
                        </div>
                    )}

                    {/* Bowler list */}
                    {bowlersLoading ? (
                        <div className="flex justify-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-indigo-600" />
                        </div>
                    ) : bowlers.length === 0 ? (
                        <div className="text-center py-16 text-gray-500">
                            {search ? 'No bowlers match your search.' : 'No bowlers found in this tournament.'}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {bowlers.map(bowler => {
                                const bal = balances[bowler.CLAVE];
                                const bowlerPkgs = getBowlerPackages(bowler.CLAVE);
                                const name = getBowlerName(bowler);

                                return (
                                    <div key={bowler.CLAVE} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                                        <div className="flex flex-col lg:flex-row lg:items-start gap-3">

                                            {/* Identity */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-bold text-gray-900 text-sm">TAT #{bowler.FICHANAL}</span>
                                                    <span className="text-gray-800 text-sm font-medium">{name}</span>
                                                    {bowler.USBCNUM && bowler.USBCNUM.trim() && (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 text-[11px] font-semibold">
                                                            USBC #{bowler.USBCNUM.trim()}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-0.5">
                                                    {bowler.EMAIL || 'No email'}
                                                </div>
                                                {bowlerRecruiterMap[bowler.CLAVE]?.name && (
                                                    <div className="text-xs text-indigo-600 mt-0.5 font-medium">
                                                        Recruited by: {bowlerRecruiterMap[bowler.CLAVE].name}
                                                    </div>
                                                )}
                                                <div className="relative inline-block mt-1.5">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setRecruiterPopover(prev => prev === bowler.CLAVE ? null : bowler.CLAVE); }}
                                                        disabled={settingRecruiter === bowler.CLAVE}
                                                        className={`text-xs font-semibold px-2.5 py-1 rounded-lg border transition disabled:opacity-50 ${discountCodeBowlers.has(bowler.CLAVE) ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'}`}
                                                    >
                                                        {settingRecruiter === bowler.CLAVE ? 'Saving…' : 'Add to Group'}
                                                    </button>
                                                    {recruiterPopover === bowler.CLAVE && (
                                                        <div
                                                            className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl min-w-[210px] py-1"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {tournamentRecruiters.length === 0 ? (
                                                                <div className="px-3 py-2 text-xs text-gray-400">No recruiters for this tournament</div>
                                                            ) : (
                                                                tournamentRecruiters.map((r: any) => (
                                                                    <button
                                                                        key={r.CVEJUGADOR}
                                                                        onClick={() => handleSetRecruiter(bowler.CLAVE, selectedTournament!.CLAVE, r.CVEJUGADOR, r.NOMBRECOMPLETO)}
                                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 transition"
                                                                    >
                                                                        <div className="font-medium text-gray-900">{r.NOMBRECOMPLETO}</div>
                                                                        <div className="text-xs text-gray-400">TAT #{r.FICHANAL}</div>
                                                                    </button>
                                                                ))
                                                            )}
                                                            {bowlerRecruiterMap[bowler.CLAVE]?.clave != null && (
                                                                <button
                                                                    onClick={() => handleSetRecruiter(bowler.CLAVE, selectedTournament!.CLAVE, null, null)}
                                                                    className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-50 border-t border-gray-100 transition"
                                                                >
                                                                    Remove from group
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Packages */}
                                            <div className="lg:w-60 flex-shrink-0">
                                                {bowlerPkgs.length === 0 ? (
                                                    <span className="text-xs text-gray-400 italic">No packages</span>
                                                ) : (
                                                    <div className="space-y-0.5">
                                                        {bowlerPkgs.map((pkg, i) => (
                                                            <div key={i} className="flex items-center justify-between gap-2 text-xs">
                                                                <span className="text-gray-700 truncate">{pkg.DESCRIPCION}</span>
                                                                <span className="text-gray-500 flex-shrink-0">{formatCurrency(pkg.COSTO)}</span>
                                                            </div>
                                                        ))}
                                                        <div className="text-xs font-semibold text-gray-500 pt-0.5 border-t border-gray-100 mt-1">
                                                            {bowlerPkgs.length} package{bowlerPkgs.length !== 1 ? 's' : ''}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Balance */}
                                            <div className="lg:w-44 flex-shrink-0">
                                                {!bal || bal.loading ? (
                                                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400" />
                                                        Loading balance...
                                                    </div>
                                                ) : (
                                                    <div className="text-xs space-y-0.5">
                                                        <div className="flex justify-between gap-2">
                                                            <span className="text-gray-400">Bought</span>
                                                            <span className="font-medium text-gray-700">{formatCurrency(bal.COMPRADO)}</span>
                                                        </div>
                                                        <div className="flex justify-between gap-2">
                                                            <span className="text-gray-400">Paid</span>
                                                            <span className="font-medium text-green-700 flex items-center gap-1.5">
                                                                {formatCurrency(bal.PAGADO)}
                                                                {bal.FOURCC && (
                                                                    <span className="text-xs font-mono bg-slate-100 text-slate-500 border border-slate-200 px-1.5 py-0.5 rounded" title="Last 4 digits of card used">
                                                                        •••• {bal.FOURCC}
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between gap-2 border-t border-gray-100 pt-0.5">
                                                            <span className="text-gray-500 font-medium">Balance</span>
                                                            <span className={`font-bold ${bal.BALANCE > 0 ? 'text-red-600' : bal.BALANCE < 0 ? 'text-blue-600' : 'text-gray-600'}`}>
                                                                {formatCurrency(bal.BALANCE)}
                                                            </span>
                                                        </div>
                                                        {isGermanTournament && (() => {
                                                            const recorded = getBowlerTax(bowler.CLAVE);
                                                            const bowlerCost = getBowlerPackages(bowler.CLAVE).reduce((s, p) => s + p.COSTO, 0);
                                                            const simulated = bowlerCost * (taxRate / 100);
                                                            return (
                                                                <div className="mt-2 pt-1.5 border-t border-yellow-100 space-y-0.5">
                                                                    <div className="flex justify-between gap-2">
                                                                        <span className="text-yellow-500 font-medium">Tax collected</span>
                                                                        <span className="font-bold text-yellow-600">{formatCurrency(recorded)}</span>
                                                                    </div>
                                                                    <div className="flex justify-between gap-2">
                                                                        <span className="text-yellow-400">At {taxRate}% (cost)</span>
                                                                        <span className="font-semibold text-yellow-500">{formatCurrency(simulated)}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Actions: POS + Remove */}
                                            <div className="lg:w-28 flex-shrink-0 flex items-start justify-end lg:justify-center gap-2">
                                                <button
                                                    onClick={() => navigate(`/admin/entry-pos/${selectedTournament!.CLAVE}/${bowler.CLAVE}`)}
                                                    className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 p-1.5 rounded-lg border border-indigo-200 transition"
                                                    title="Open POS"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2M9 7V5a2 2 0 012-2h4l3 3v3M9 7h6m-6 4h3m-3 4h6" />
                                                    </svg>
                                                </button>
                                                {bowler.PACKAGE_COUNT === 0 && (
                                                    <button
                                                        onClick={() => handleRemoveBowler(bowler.CLAVE, bowler.FICHANAL)}
                                                        disabled={removingId === bowler.CLAVE}
                                                        className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs font-semibold px-3 py-1.5 rounded-lg border border-red-200 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                                    >
                                                        {removingId === bowler.CLAVE ? 'Removing…' : 'Remove'}
                                                    </button>
                                                )}
                                            </div>

                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Pagination */}
                    {!bowlersLoading && totalPages > 1 && (
                        <div className="flex items-center justify-between mt-6">
                            <span className="text-sm text-gray-600">
                                {totalCount} bowler{totalCount !== 1 ? 's' : ''} · Page {page} of {totalPages}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={page <= 1}
                                    className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page >= totalPages}
                                    className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            )}

        </div>

        {/* ── Add Bowler to Tournament Modal ──────────────────────────── */}
        {showAddBowlerModal && selectedTournament && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAddBowlerModal(false)}>
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>

                    {/* Modal header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                        <div className="flex items-center gap-3 min-w-0">
                            {addBowlerStep === 'division' && (
                                <button onClick={() => setAddBowlerStep('search')} className="text-gray-400 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 transition flex-shrink-0">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                            )}
                            <div className="min-w-0">
                                <h2 className="text-lg font-bold text-gray-900">
                                    {addBowlerStep === 'search' ? 'Add Bowler to Tournament' :
                                     addBowlerStep === 'division' ? 'Select Division' : 'Bowler Added!'}
                                </h2>
                                <p className="text-xs text-gray-500 truncate">{selectedTournament.NOMBRE}</p>
                            </div>
                        </div>
                        <button onClick={() => setShowAddBowlerModal(false)} className="flex-shrink-0 text-gray-400 hover:text-gray-700 transition p-1 rounded-lg hover:bg-gray-100 ml-3">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* ── Step: search ── */}
                    {addBowlerStep === 'search' && (
                        <>
                            <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0">
                                <input
                                    type="text"
                                    value={addBowlerQuery}
                                    onChange={e => handleAddBowlerQueryChange(e.target.value)}
                                    placeholder="Search by TAT#, name, or email…"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    autoFocus
                                />
                            </div>
                            <div className="flex-1 overflow-y-auto px-6 py-3">
                                {addBowlerCandidatesLoading ? (
                                    <div className="flex justify-center py-10">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-4 border-indigo-600" />
                                    </div>
                                ) : addBowlerQuery.trim().length < 2 ? (
                                    <p className="text-center text-gray-400 text-sm py-10">Type at least 2 characters to search</p>
                                ) : addBowlerCandidates.length === 0 ? (
                                    <p className="text-center text-gray-400 text-sm py-10">No bowlers found</p>
                                ) : (
                                    <div className="space-y-2">
                                        {addBowlerCandidates.map((b: any) => {
                                            const inTournament = b.IN_TOURNAMENT > 0;
                                            return (
                                                <div key={b.CLAVE} className="flex items-center justify-between gap-3 py-2.5 border-b border-gray-50 last:border-0">
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-xs font-bold text-gray-500">TAT #{b.FICHANAL}</span>
                                                            <span className="text-sm font-medium text-gray-900 truncate">{b.NOMBRECOMPLETO}</span>
                                                        </div>
                                                        <p className="text-xs text-gray-400 truncate">{b.EMAIL}</p>
                                                    </div>
                                                    <div className="flex-shrink-0">
                                                        {inTournament ? (
                                                            <span className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-green-100 text-green-700">
                                                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                </svg>
                                                                Enrolled
                                                            </span>
                                                        ) : (
                                                            <button
                                                                onClick={() => handleSelectBowlerToAdd(b)}
                                                                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition"
                                                            >
                                                                Select
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            {!addBowlerCandidatesLoading && addBowlerCandidatesTotal > 10 && (
                                <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 flex-shrink-0">
                                    <span className="text-xs text-gray-400">
                                        {addBowlerCandidatesTotal} results · Page {addBowlerCandidatesPage} of {Math.ceil(addBowlerCandidatesTotal / 10)}
                                    </span>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => { const p = addBowlerCandidatesPage - 1; setAddBowlerCandidatesPage(p); loadAddBowlerCandidates(addBowlerQuery, p); }}
                                            disabled={addBowlerCandidatesPage <= 1}
                                            className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                        >Previous</button>
                                        <button
                                            onClick={() => { const p = addBowlerCandidatesPage + 1; setAddBowlerCandidatesPage(p); loadAddBowlerCandidates(addBowlerQuery, p); }}
                                            disabled={addBowlerCandidatesPage >= Math.ceil(addBowlerCandidatesTotal / 10)}
                                            className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                        >Next</button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── Step: division ── */}
                    {addBowlerStep === 'division' && selectedBowlerToAdd && (
                        <div className="flex-1 overflow-y-auto px-6 py-5">
                            {/* Selected bowler summary */}
                            <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 mb-5">
                                <p className="text-xs text-indigo-500 font-semibold uppercase tracking-wide mb-0.5">Adding</p>
                                <p className="font-bold text-gray-900">{selectedBowlerToAdd.NOMBRECOMPLETO}</p>
                                <p className="text-xs text-gray-500">TAT #{selectedBowlerToAdd.FICHANAL} · {selectedBowlerToAdd.EMAIL}</p>
                            </div>

                            <p className="text-sm font-semibold text-gray-700 mb-3">Select a division:</p>

                            {divisionsLoading ? (
                                <div className="flex justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-4 border-indigo-600" />
                                </div>
                            ) : tournamentDivisions.length === 0 ? (
                                <p className="text-center text-gray-400 text-sm py-8">No divisions found for this tournament</p>
                            ) : (
                                <div className="flex flex-wrap gap-2 mb-6">
                                    {tournamentDivisions.map((d: any) => (
                                        <button
                                            key={d.CLAVE}
                                            onClick={() => setSelectedDivisionToAdd(d.CLAVE)}
                                            className={`px-4 py-2.5 rounded-xl text-sm font-semibold border-2 transition ${
                                                selectedDivisionToAdd === d.CLAVE
                                                    ? 'bg-indigo-600 border-indigo-600 text-white shadow-md'
                                                    : 'bg-white border-gray-300 text-gray-700 hover:border-indigo-400 hover:text-indigo-700'
                                            }`}
                                        >
                                            {d.DESCRIPCION}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {addBowlerError && (
                                <p className="text-sm text-red-600 text-center mb-3">{addBowlerError}</p>
                            )}

                            <button
                                onClick={handleAddBowlerSubmit}
                                disabled={!selectedDivisionToAdd || addingBowler}
                                className={`w-full py-3.5 px-4 rounded-xl font-bold text-white text-sm transition shadow-md ${
                                    !selectedDivisionToAdd || addingBowler
                                        ? 'bg-indigo-300 cursor-not-allowed'
                                        : 'bg-indigo-600 hover:bg-indigo-700 active:scale-95'
                                }`}
                            >
                                {addingBowler ? 'Adding…' : 'Add to Tournament'}
                            </button>
                        </div>
                    )}

                    {/* ── Step: success ── */}
                    {addBowlerStep === 'success' && selectedBowlerToAdd && (
                        <div className="flex-1 px-6 py-10 text-center">
                            <div className="flex justify-center mb-4">
                                <div className="bg-green-100 rounded-full p-4">
                                    <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Bowler Added!</h3>
                            <p className="text-gray-600 text-sm mb-1">
                                <strong>{selectedBowlerToAdd.NOMBRECOMPLETO}</strong> (TAT #{selectedBowlerToAdd.FICHANAL})
                            </p>
                            <p className="text-gray-500 text-sm mb-6">
                                has been added to <strong>{selectedTournament.NOMBRE}</strong>.<br />
                                You can now open their POS to add packages.
                            </p>
                            <div className="flex flex-col gap-3">
                                <button
                                    onClick={() => {
                                        setShowAddBowlerModal(false);
                                        navigate(`/admin/entry-pos/${selectedTournament.CLAVE}/${selectedBowlerToAdd.CLAVE}`);
                                    }}
                                    className="w-full py-3 px-4 rounded-xl font-bold text-white text-sm bg-indigo-600 hover:bg-indigo-700 transition shadow-md"
                                >
                                    Open POS for {selectedBowlerToAdd.NOMBRECOMPLETO}
                                </button>
                                <button
                                    onClick={() => handleOpenAddBowlerModal()}
                                    className="w-full py-3 px-4 rounded-xl font-bold text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                                >
                                    Add Another Bowler
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        )}

        {/* ── Add Recruiter to Tournament Modal ─────────────────────── */}
        {showAddRecruiterModal && selectedTournament && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAddRecruiterModal(false)}>
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                    {/* Modal header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Add Recruiter to Tournament</h2>
                            <p className="text-xs text-gray-500 mt-0.5 leading-tight">{selectedTournament!.NOMBRE}</p>
                        </div>
                        <button onClick={() => setShowAddRecruiterModal(false)} className="text-gray-400 hover:text-gray-700 transition p-1 rounded-lg hover:bg-gray-100">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Search */}
                    <div className="px-6 py-3 border-b border-gray-100">
                        <input
                            type="text"
                            value={recruiterCandidatesSearch}
                            onChange={e => handleRecruiterModalSearchChange(e.target.value)}
                            placeholder="Search by name or TAT ID…"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                            autoFocus
                        />
                    </div>

                    {/* List */}
                    <div className="flex-1 overflow-y-auto px-6 py-3">
                        {recruiterCandidatesLoading ? (
                            <div className="flex justify-center py-10">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-4 border-violet-600" />
                            </div>
                        ) : recruiterCandidates.length === 0 ? (
                            <div className="text-center py-10 text-gray-400 text-sm">
                                {recruiterCandidatesSearch ? 'No bowlers match your search.' : 'No bowlers found.'}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {recruiterCandidates.map((b: any) => {
                                    const name = (b.NOMBRECOMPLETO && b.NOMBRECOMPLETO.trim()) || `${b.NOMBRE || ''} ${b.APELLIDOP || ''}`.trim() || 'Unknown';
                                    const isRecruiter = b.IS_RECRUITER > 0;
                                    return (
                                        <div key={b.CLAVE} className="flex items-center justify-between gap-3 py-2.5 border-b border-gray-50 last:border-0">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-xs font-bold text-gray-500">TAT #{b.FICHANAL}</span>
                                                    <span className="text-sm font-medium text-gray-900 truncate">{name}</span>
                                                </div>
                                            </div>
                                            <div className="flex-shrink-0">
                                                {isRecruiter ? (
                                                    <span className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-green-100 text-green-700">
                                                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                        Already added
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={() => handleAddTournamentRecruiter(b.CLAVE)}
                                                        disabled={addingRecruiter === b.CLAVE}
                                                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-pink-600 hover:bg-pink-700 text-white transition disabled:opacity-50"
                                                    >
                                                        {addingRecruiter === b.CLAVE ? 'Adding…' : 'Add'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {!recruiterCandidatesLoading && recruiterCandidatesTotal > 10 && (
                        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
                            <span className="text-xs text-gray-400">
                                {recruiterCandidatesTotal} bowlers · Page {recruiterCandidatesPage} of {Math.ceil(recruiterCandidatesTotal / 10)}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleRecruiterCandidatesPageChange(recruiterCandidatesPage - 1)}
                                    disabled={recruiterCandidatesPage <= 1}
                                    className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => handleRecruiterCandidatesPageChange(recruiterCandidatesPage + 1)}
                                    disabled={recruiterCandidatesPage >= Math.ceil(recruiterCandidatesTotal / 10)}
                                    className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )}
        </>
    );
};

export default AdminTournamentEntries;
